import { bridge } from "@multiweb/reactCore/bridge";
import {
    IIsNumberCharactersOnlyValidParams,
    IIsNumbersOnlyValidParams,
    IValidateCharsDeletedParams,
    IValidateMaxLengthParams,
    IValidateMaxValueParams,
    IValidateMinValueParams,
    IValidateRegexParams,
} from "./validationFunctionTypes";

const { createBigNumberProxy } = bridge;

/**
 * Zvaliduje, zda zadaná řetězcová hodnota není delší, než maximální délka.
 *
 * @param value Řetězec k validaci.
 * @param maxLength Maximální povolená délka pro `value`.
 * @return `true`, pokud validace projde.
 */
export const isMaxLengthValid = ({ value, maxLength }: IValidateMaxLengthParams): boolean => {
    return !value || (value.length <= maxLength);
};

/**
 * Zvaliduje, zda zadaná řetězcová hodnota vyhovuje danému regulárnímu výrazu.
 *
 * @param value Řetězec k validaci.
 * @param regex Regulární výraz, vůči kterému se `value` validuje.
 * @return `true`, pokud validace projde.
 */
export const isRegexValid = ({ value, regex }: IValidateRegexParams): boolean => {
    return !value || regex.test(value);
};

/**
 * Zvaliduje, zda zadaná řetězcová hodnota obsahuje pouze čísla.
 *
 * @param value Řetězec k validaci.
 * @return `true`, pokud validace projde.
 */
export const isNumbersOnlyValid = ({ value }: IIsNumbersOnlyValidParams): boolean => {
    return !value || /^\d+$/.test(value);
};

/**
 * Kontroluje, jestli byl z hodnoty 'value' smazán nějaký znak porovnáním délky hodnoty 'targetValue'.
 * Pokud je délka 'value' menší než délka 'targetValue', vrací 'true', v opačném případě 'false'.
 * @param value validovaná hodnota vůči 'targetValue'
 * @param targetValue
 */
export const charsDeleted = ({ value, targetValue }: IValidateCharsDeletedParams): boolean => {
    return !!value && !!targetValue && (value.length < targetValue.length);
};

/**
 * Kontroluje, zda zadaná řetězcová hodnota obsahuje pouze povolené znaky.
 *
 * @param value Řetězcová hodnota.
 * @param isInteger Příznak, zda se mají validovat pouze celá čísla.
 */
export const isNumberCharactersOnlyValid = ({ value, isInteger }: IIsNumberCharactersOnlyValidParams): boolean => {
    const regex = isInteger
        ? /^[+-]?\d*$/
        : /^[+-]?\d*(\.)?\d*$/;
    return regex.test(value);
};

/**
 * Kontroluje, zda je zadaná hodnota větší nebo rovna nejmenší povolené hodnotě.
 * V případě že je zadaná hodnota NaN, validace projde.
 *
 * @param value Zadaná hodnota.
 * @param minValue Nejmenší povolená hodnota
 */
export const isMinValueValid = ({ value, minValue }: IValidateMinValueParams) => {
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) {
        return true;
    }
    return numberValue >= minValue;
};

/**
 * Kontroluje, zda je zadaná hodnota reprezentující BigNumber větší nebo rovna nejmenší povolené hodnotě.
 * V případě že je zadaná hodnota NaN, validace projde.
 *
 * @param value Zadaná hodnota - řetězec který se předá konstruktoru BigNumberProxy.
 * @param minValue Nejmenší povolená hodnota
 */
export const isMinValueBigNumberValid = ({ value, minValue }: IValidateMinValueParams) => {
    const bigNumberValue = createBigNumberProxy(value);
    if (bigNumberValue.isNaN()) {
        return true;
    }
    return bigNumberValue.gte(minValue);
};

/**
 * Kontroluje, zda je zadaná hodnota menší nebo rovna největší povolené hodnotě.
 * V případě že je zadaná hodnota NaN, validace projde.
 *
 * @param value Zadaná hodnota.
 * @param maxValue Největší povolená hodnota
 */
export const isMaxValueValid = ({ value, maxValue }: IValidateMaxValueParams) => {
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) {
        return true;
    }
    return numberValue <= maxValue;
};

/**
 * Kontroluje, zda je zadaná hodnota reprezentující BigNumber menší nebo rovna největší povolené hodnotě.
 * V případě že je zadaná hodnota NaN, validace projde.
 *
 * @param value Zadaná hodnota - řetězec který se předá konstruktoru BigNumberProxy.
 * @param maxValue Největší povolená hodnota
 */
export const isMaxValueBigNumberValid = ({ value, maxValue }: IValidateMaxValueParams) => {
    const bigNumberValue = createBigNumberProxy(value);
    if (bigNumberValue.isNaN()) {
        return true;
    }
    return bigNumberValue.lte(maxValue);
};
